import { useEffect, useMemo, useRef, useState } from "react";
import { AiOutlineUnlock } from "react-icons/ai";
import { IoTrashOutline } from "react-icons/io5";
import "@style/Admin.scss";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import ConfirmModal from "./ConfirmModal";
import Modal from "@modalSrc/Modal"; // Adjust path to your generic Modal
import ModalFormFields from "@modalSrc/ModalFormFields"; // Import for fields rendering
import Enums from "@/enums";
import DateTimePicker from "@elements/DateTimePicker";
import { useI18n } from "@/i18n/I18nProvider";
import { filterActiveLanguages, getLanguagePickerLabel } from "@/utils/languageUtils";
import { apiMessage } from "@/utils/apiMessage";

function safeReadCurrentUserFromSession() {
  try {
    const userData = window?.sessionStorage?.getItem("User");
    if (!userData) return null;
    return JSON.parse(atob(userData));
  } catch {
    return null;
  }
}

type UserModel = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  showHelp: boolean;
  jobId: number | null;
  supervisorId: number | null;
  languageId: number | null;
  assignedLanguageIds?: number[];
  supervisorName: string;
  job?: string;
  hireDate?: string;
  terminationDate?: string;
};

type UserSavePayload = {
  firstName: string;
  lastName: string;
  email: string;
  showHelp?: boolean;
  jobId?: number | null;
  hireDate?: string;
  terminationDate?: string;
  companyId?: number;
  userId?: number;
  id?: number;
  supervisorId?: number | null;
  languageId?: number | null;
};

type LanguageOption = {
  id: number;
  name?: string;
  abbrev?: string;
  supported?: boolean;
  active?: boolean;
};

function getJobDisplayName(job: any) {
  return String(job?.jobName || job?.name || job?.title || "").trim();
}

export default function UserEditModal({ isOpen, onClose, userId, companyData, updateSpinner, adminPerms }) {
  const [user, setUser] = useState<UserModel>({
    id: 0,
    firstName: "",
    lastName: "",
    email: "",
    showHelp: true,
    jobId: null,
    supervisorId: null,
    languageId: 1,
    supervisorName: ""
  });
  const [originalUser, setOriginalUser] = useState<Partial<UserModel>>({});
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Change password states
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword1, setNewPassword1] = useState("");
  const [newPassword2, setNewPassword2] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [isPasswordFormValid, setIsPasswordFormValid] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [allLanguages, setAllLanguages] = useState<LanguageOption[]>([]);
  const [assignedLanguageIds, setAssignedLanguageIds] = useState<number[]>([]);
  const { t } = useI18n();
  const userLoadKeyRef = useRef<string>("");
  const isLoadingUserRef = useRef(false);

  useEffect(() => {
    const companyScopeId = companyData?.companyId ?? companyData?.id ?? 0;

    if (!isOpen) {
      userLoadKeyRef.current = "";
      isLoadingUserRef.current = false;
      return;
    }

    if (userId && userId !== 0) {
      const nextLoadKey = `${userId}:${companyScopeId}`;
      if (userLoadKeyRef.current === nextLoadKey || isLoadingUserRef.current) return;

      isLoadingUserRef.current = true;
      getUserAsync()
        .finally(() => {
          isLoadingUserRef.current = false;
          userLoadKeyRef.current = nextLoadKey;
        });
      return;
    }

    if (userId === 0) {
      // New user: clear form
      setUser({
        id: 0,
        firstName: "",
        lastName: "",
        email: "",
        showHelp: true,
        jobId: null,
        supervisorId: null,
        languageId: 1,
        supervisorName: ""
      });
      setOriginalUser({
        id: 0,
        firstName: "",
        lastName: "",
        email: "",
        showHelp: true,
        jobId: null,
        supervisorId: null,
        languageId: 1,
        supervisorName: ""
      });
    }
  }, [isOpen, userId, companyData?.companyId, companyData?.id]);

  const normalizedAdminPerms = Array.isArray(adminPerms)
    ? adminPerms.map((perm: number | { value?: number; permId?: number }) => Number(
      typeof perm === "object" ? (perm?.value ?? perm?.permId ?? 0) : perm
    ))
    : [];
  const isSystemAdmin = normalizedAdminPerms.includes(Enums.ROLE_PERMS.SystemAdmin);
  const isCompanyAdmin = normalizedAdminPerms.includes(Enums.ROLE_PERMS.CompanyAdmin);
  const canManageTranslatorLanguages = isSystemAdmin || normalizedAdminPerms.includes(Enums.ROLE_PERMS.Translator);
  const isAdminLike = isSystemAdmin || isCompanyAdmin;
  const currentUserId = (() => {
    const decodedUser = safeReadCurrentUserFromSession();
    return decodedUser?.userId || decodedUser?.id || null;
  })();
  const canChangePassword = isAdminLike
    || (currentUserId && ((user?.id && currentUserId === user.id) || (userId && currentUserId === userId)));
  const canDeleteUser = isSystemAdmin
    || isCompanyAdmin
    || normalizedAdminPerms.includes(Enums.ROLE_PERMS.DeleteUser);

  // Validate password form
  useEffect(() => {
    if (isSystemAdmin) {
      // System admin only needs new passwords to match
      if (newPassword1 && newPassword2) {
        if (newPassword1 !== newPassword2) {
          setPasswordMessage(t("Passwords must match."));
          setIsPasswordFormValid(false);
        } else {
          setPasswordMessage("");
          setIsPasswordFormValid(true);
        }
      } else {
        setIsPasswordFormValid(false);
        setPasswordMessage("");
      }
    } else {
      // Regular users need old password and matching new passwords
      if (oldPassword && newPassword1 && newPassword2) {
        if (newPassword1 !== newPassword2) {
          setPasswordMessage(t("Passwords must match."));
          setIsPasswordFormValid(false);
        } else {
          setPasswordMessage("");
          setIsPasswordFormValid(true);
        }
      } else {
        setIsPasswordFormValid(false);
        setPasswordMessage("");
      }
    }
  }, [oldPassword, newPassword1, newPassword2, isSystemAdmin, t]);

  useEffect(() => {
    if (!isOpen) return;

    let isMounted = true;
    (async () => {
      try {
        const languagesResp = await RightTimeApi.getAsync("/Languages");
        if (!isMounted) return;

        if (languagesResp?.ok && Array.isArray(languagesResp.jsonData)) {
          setAllLanguages(languagesResp.jsonData);
        } else {
          setAllLanguages([]);
        }
      } catch {
        if (isMounted) {
          setAllLanguages([]);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const supportedLanguages = useMemo(() => {
    const normalized = Array.isArray(allLanguages)
      ? allLanguages
          .filter((lang) => Number(lang?.id) > 0)
          .map((lang) => ({
            id: Number(lang.id),
            name: lang.name,
            abbrev: lang.abbrev,
            supported: lang.supported,
            active: lang.active,
          }))
          .sort((a, b) => a.id - b.id)
      : [];

    if (normalized.length > 0) {
      return normalized;
    }

    return [
      { id: 1, name: "English", abbrev: "eng", ietf: "en-US", supported: true, active: true },
    ];
  }, [allLanguages]);

  const interfaceLanguageOptions = useMemo(
    () => filterActiveLanguages(supportedLanguages) as LanguageOption[],
    [supportedLanguages]
  );

  const translatorLanguageOptions = useMemo(() => {
    if (!canManageTranslatorLanguages) return [];

    return supportedLanguages.filter((lang) => lang.id > 1);
  }, [supportedLanguages, canManageTranslatorLanguages]);

  // Check for changes and validate required fields
  useEffect(() => {
    if (originalUser && user) {
      const fieldsToCheck = ['firstName', 'lastName', 'email', 'showHelp', 'jobId', 'supervisorId', 'languageId', 'hireDate', 'terminationDate'];
      const hasChanges = fieldsToCheck.some(field =>
        originalUser[field] !== user[field]
      );
      setHasChanges(hasChanges);
    }
  }, [user, originalUser]);

  if (userId === null || userId === undefined) return null;

  // Validate required fields
  const isFormValid = () => {
    return user.firstName?.trim() && user.lastName?.trim() && user.email?.trim();
  };

  async function getUserAsync() {
    if (!userId || userId===0) {
      return;
    }
    if (updateSpinner) updateSpinner(true);
    try {
      let endpoint = `/User/${userId}`;
      if( companyData?.companyId) {
        endpoint += `?companyId=${companyData.companyId}`;
      }
      const resp = await RightTimeApi.getAsync(endpoint);
      if (resp.ok) {
        // Use jobId directly from API response
        let jobId = resp.jsonData.jobId || null;

        // If we have a valid job (with ID and name), check if it exists in local list
        if (jobId && resp.jsonData.job && companyData?.companyJobs) {
          const existingJob = companyData.companyJobs.find(job => job.id === jobId);

          // If job doesn't exist in local list, add it
          if (!existingJob) {
            const displayName = String(resp.jsonData.job || resp.jsonData.title || "").trim();
            const newJob = {
              id: jobId,
              jobName: displayName,
              name: displayName,
              title: displayName,
            };
            companyData.companyJobs.push(newJob);
          }
        }
        const loadedUser = {
          ...resp.jsonData,
          showHelp: resp.jsonData.showHelp ?? true,
          languageId: resp.jsonData.languageId ?? 1,
          assignedLanguageIds: Array.isArray(resp.jsonData.assignedLanguageIds)
            ? resp.jsonData.assignedLanguageIds.map((id: number) => Number(id))
            : [],
        };
        setUser(loadedUser);
        setOriginalUser({ ...loadedUser });
        setAssignedLanguageIds(loadedUser.assignedLanguageIds ?? []);
      }
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function saveUserAsync() {
    // Check if required fields are filled
    if (!isFormValid()) {
      toast.error(t("Missing Required Data"));
      return;
    }

    let putDataModel: UserSavePayload = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      showHelp: user.showHelp,
      languageId: user.languageId ?? 1,
    };
    if(user.hireDate)
      putDataModel.hireDate = user.hireDate;
    if(user.terminationDate)
      putDataModel.terminationDate = user.terminationDate;

    // Include companyId when available so backend can update company-scoped fields like JobId.
    const effectiveCompanyId = companyData?.companyId ?? companyData?.id;
    if (effectiveCompanyId) {
      putDataModel.companyId = effectiveCompanyId;
    }

    // Only include ID fields if they are greater than 0
    if (user.id > 0) {
      putDataModel.userId = user.id;
      putDataModel.id = user.id;
    }

    // Only include optional fields if they are not null/undefined
    if (user.jobId !== null && user.jobId !== undefined) {
      putDataModel.jobId = user.jobId;
    }

    if (user.supervisorId !== null && user.supervisorId !== undefined) {
      putDataModel.supervisorId = user.supervisorId;
    }

    putDataModel.languageId = user.languageId ?? 1;

    if (updateSpinner) updateSpinner(true);
    try {
      const resp = await RightTimeApi.sendAsync("/User", putDataModel);
      if (resp.ok) {
        toast.success(t("User updated successfully!"));
        setOriginalUser({ ...user });

        const targetUserId = Number(user?.id || userId || 0);
        const currentUserIdNumber = Number(currentUserId || 0);
        const previousLanguageId = Number(originalUser?.languageId || 1);
        const nextLanguageId = Number(user?.languageId || 1);
        const didCurrentUserChangeOwnLanguage =
          currentUserIdNumber > 0 &&
          targetUserId > 0 &&
          currentUserIdNumber === targetUserId &&
          previousLanguageId !== nextLanguageId;

        if (didCurrentUserChangeOwnLanguage) {
          try {
            const rawUser = window.sessionStorage.getItem("User");
            if (rawUser) {
              const decodedUser = JSON.parse(atob(rawUser));
              const updatedUser = {
                ...decodedUser,
                languageId: nextLanguageId,
                defaultLanguageId: nextLanguageId,
              };
              window.sessionStorage.setItem("User", btoa(JSON.stringify(updatedUser)));
            }
          } catch {
          }

          window.location.reload();
          return;
        }

        // If this was a new user (id === 0), pass back the new user ID for selection
        const newUserId = user.id === 0 && resp.jsonData?.id ? resp.jsonData.id : null;
        if (onClose) onClose(true, newUserId); // Pass true to indicate successful save, and the new user ID
      } else {
        const failMsg = apiMessage(resp);
        if (failMsg)
          toast.error(`${t("Failed to update user. Reason:")} ${failMsg}`);
        else
          toast.error(t("Failed to update user. Please try again."));
      }
    } catch (error) {
      toast.error(t("An error occurred while updating user."));
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function deleteUserAsync() {
    if (!user.id || user.id === 0) {
      toast.error(t("Cannot delete a new user that hasn't been saved yet."));
      return;
    }

    setShowDeleteConfirm(true);
  }

  async function handleDeleteConfirm() {
    setShowDeleteConfirm(false);
    setIsDeleting(true);
    if (updateSpinner) updateSpinner(true);

    try {
      const resp = await RightTimeApi.deleteAsync(`/User/${user.id}`);
      if (resp.ok) {
        toast.success(t("User deleted successfully!"));
        if (onClose) onClose(true, null); // Pass true to indicate successful operation, null for no new user ID
      } else {
        toast.error(t("Failed to delete user. Please try again."));
      }
    } catch (error) {
      toast.error(t("An error occurred while deleting user."));
    } finally {
      setIsDeleting(false);
      if (updateSpinner) updateSpinner(false);
    }
  }

  function handleDeleteCancel() {
    setShowDeleteConfirm(false);
  }

  function handleUserPropertyChange(field, value, type = 'string') {
    let processedValue = value;
    if (type === 'int' && value !== null && value !== undefined) {
      processedValue = parseInt(value) || null;
    }

    setUser(prev => ({
      ...prev,
      [field]: processedValue
    }));
  }

  function handleClose() {
    if (hasChanges) {
      if (window.confirm(t("You have unsaved changes. Are you sure you want to close?"))) {
        resetForm();
        if (onClose) onClose();
      }
    } else {
      resetForm();
      if (onClose) onClose();
    }
  }

  function resetForm() {
    // Reset change password form when closing
    setShowChangePassword(false);
    setOldPassword("");
    setNewPassword1("");
    setNewPassword2("");
    setPasswordMessage("");
  }

  function handleChangePasswordClick() {
    if (!canChangePassword) return;
    setShowChangePassword(!showChangePassword);
    // Reset form when toggling
    if (!showChangePassword) {
      setOldPassword("");
      setNewPassword1("");
      setNewPassword2("");
      setPasswordMessage("");
    }
  }

  async function handleGeneratePasswordClick() {
    if (!user.id) return;

    if (updateSpinner) updateSpinner(true);
    try {
      const resp = await RightTimeApi.patchAsync(`User/${user.id}/ResetPassword`);
      if (resp && resp.ok) {
        await navigator.clipboard.writeText(resp.jsonData.pw);
        toast.success(t("Password reset. New password copied to clipboard."));
        setShowChangePassword(false);
        setOldPassword("");
        setNewPassword1("");
        setNewPassword2("");
        setPasswordMessage("");
      } else {
        toast.error(t("Failed to generate password. Please try again."));
      }
    } catch (error) {
      toast.error(t("An error occurred while generating password."));
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  async function handlePasswordSubmit(e) {
    e.preventDefault();

    if ((isSystemAdmin && newPassword1 && newPassword2 && newPassword1 === newPassword2) ||
      (!isSystemAdmin && oldPassword && newPassword1 && newPassword2 && newPassword1 === newPassword2)) {
      const data = {
        userId: user.id || 0,
        oldPassword: isSystemAdmin ? "" : oldPassword,
        newPassword: newPassword1,
        newUser: false,
      };

      if (updateSpinner) updateSpinner(true);
      try {
        const resp = await RightTimeApi.patchAsync(`/User/${user.id}/ChangePassword`, data);
        if (resp.ok) {
          toast.success(t("Password changed successfully!"));
          setShowChangePassword(false);
          setOldPassword("");
          setNewPassword1("");
          setNewPassword2("");
          setPasswordMessage("");
        } else {
          const apiMsg = apiMessage(resp);
          if (resp.status === 401 || resp.status === 400) {
            toast.error(apiMsg || t("Invalid old password"), {
              autoClose: 5000,
            });
          } else {
            toast.error(apiMsg || t("Failed to change password. Please try again."));
          }
        }
      } catch (error) {
        toast.error(t("An error occurred while changing password."));
      } finally {
        if (updateSpinner) updateSpinner(false);
      }
    } else {
      if (!isSystemAdmin && !oldPassword) toast.warning(t("Old Password is required."));
      if (!newPassword1) toast.warning(t("New Password is required."));
      if (!newPassword2) toast.warning(t("Verify New Password is required."));
      if (newPassword1 && newPassword2 && newPassword1 !== newPassword2)
        toast.warning(t("Password values must match."));
    }
  }

  async function toggleTranslatorLanguageAsync(languageId: number) {
    if (!user?.id || user.id <= 0) return;
    if (updateSpinner) updateSpinner(true);
    try {
      const resp = await RightTimeApi.putAsync("/UserLanguageToggle", {
        userId: user.id,
        languageId,
      });
      if (resp?.ok && resp.jsonData) {
        const nextAssigned = Array.isArray(resp.jsonData.assignedLanguageIds)
          ? resp.jsonData.assignedLanguageIds.map((id: number) => Number(id))
          : [];
        setAssignedLanguageIds(nextAssigned);
        setUser((current) => ({ ...current, assignedLanguageIds: nextAssigned }));
        setOriginalUser((current) => ({ ...current, assignedLanguageIds: nextAssigned }));
      } else {
        toast.error(t("Failed to update translation language assignment."));
      }
    } catch (error) {
      console.error("Error toggling translator language:", error);
      toast.error(t("Error updating translation language assignment."));
    } finally {
      if (updateSpinner) updateSpinner(false);
    }
  }

  const isEditing = userId && userId !== user.id; // Editing another user vs editing own account

  const modalTitle = showChangePassword ? t('Change Password') : `${t('User Edit')}${user.id > 0 ? ` (${user.id})` : ''}`;

  const userFields = {
    [t('First Name')]: (
      <input
        type="text"
        value={user.firstName || ''}
        onChange={(e) => handleUserPropertyChange('firstName', e.target.value)}
        name="FirstName"
        required
      />
    ),
    [t('Last Name')]: (
      <input
        type="text"
        value={user.lastName || ''}
        onChange={(e) => handleUserPropertyChange('lastName', e.target.value)}
        name="LastName"
        required
      />
    ),
    [t('Email')]: (
      <input
        type="email"
        value={user.email || ''}
        onChange={(e) => handleUserPropertyChange('email', e.target.value)}
        name="Email"
        className="emailEdit"
        required
      />
    ),
    [t('Show Help')]: (
      <input
        type="checkbox"
        checked={!!user.showHelp}
        onChange={(e) => handleUserPropertyChange('showHelp', e.target.checked)}
        name="ShowHelp"
        title={t('Show contextual help for this user')}
        aria-label={t('Show contextual help')}
      />
    ),
    [t('Job')]: (
      <select
        value={user.jobId || ''}
        onChange={(e) => handleUserPropertyChange('jobId', e.target.value, 'int')}
        name="Job"
        title={user.jobId ? getJobDisplayName(companyData?.companyJobs?.find((j) => j.id === user.jobId)) || '' : t('None')}
      >
        <option value="">{t('None')}</option>
        {companyData?.companyJobs?.map((job) => (
          <option key={job.id} value={job.id}>{getJobDisplayName(job)}</option>
        ))}
      </select>
    ),
    [t('Supervisor')]: (
      <select
        value={user.supervisorId || ''}
        onChange={(e) => handleUserPropertyChange('supervisorId', e.target.value, 'int')}
        name="Supervisor"
        title={user.supervisorId ?
          companyData?.users?.find(u => u.userId === user.supervisorId) ?
            `${companyData.users.find(u => u.userId === user.supervisorId).firstName} ${companyData.users.find(u => u.userId === user.supervisorId).lastName}`
            : ''
          : t('None')}
      >
        <option value="">{t('None')}</option>
        {companyData?.users?.filter((u) => u.userId !== user.id)?.map((supervisor) => (
          <option key={supervisor.userId} value={supervisor.userId}>
            {supervisor.firstName} {supervisor.lastName}
          </option>
        ))}
      </select>
    ),
    [t('Language')]: (
      <select
        value={user.languageId || 1}
        onChange={(e) => handleUserPropertyChange('languageId', Number(e.target.value), 'int')}
        name="Language"
        title={t('Default interface language for this user')}
      >
        {interfaceLanguageOptions.map((language) => {
          const key = Number(language.id);
          const label = getLanguagePickerLabel(language) || language.abbrev || String(key);
          return (
            <option key={key} value={key}>
              {label}
            </option>
          );
        })}
      </select>
    ),
    ...(canManageTranslatorLanguages && user.id > 0 ? {
      [t('Translation Languages')]: (
        <div className="translatorLanguageAssignments">
          {translatorLanguageOptions.length === 0 ? (
            <span>{t('No supported translation languages available.')}</span>
          ) : (
            translatorLanguageOptions.map((language) => {
              const languageId = Number(language.id);
              const isAssigned = assignedLanguageIds.includes(languageId);
              const label = getLanguagePickerLabel(language) || language.name || String(languageId);
              return (
                <label key={languageId} className="translatorLanguageOption">
                  <input
                    type="checkbox"
                    checked={isAssigned}
                    onChange={() => void toggleTranslatorLanguageAsync(languageId)}
                  />
                  <span>{label}</span>
                </label>
              );
            })
          )}
        </div>
      ),
    } : {}),
    [t('Hire Date')]: (
      <DateTimePicker
        field="HireDate"
        pickerTitle="Hire Date"
        value={user.hireDate ? user.hireDate.substring(0, 10) : ''}
        onChange={(e) => handleUserPropertyChange('hireDate', e?.target?.value || '')}
        dateOnly
        name="HireDate"
      />
    ),
    [t('Termination Date')]: (
      <DateTimePicker
        field="TerminationDate"
        pickerTitle="Termination Date"
        value={user.terminationDate ? user.terminationDate.substring(0, 10) : ''}
        onChange={(e) => handleUserPropertyChange('terminationDate', e?.target?.value || '')}
        dateOnly
        name="TerminationDate"
      />
    )
  };

  const passwordFields = {
    ...(!isSystemAdmin && {
      [t('Old Password')]: (
        <input
          type="password"
          value={oldPassword}
          onChange={(e) => setOldPassword(e.target.value)}
          placeholder={t('Enter current password')}
          required
        />
      ),
    }),
    [t('New Password')]: (
      <input
        type="password"
        value={newPassword1}
        className="passwordInput"
        onChange={(e) => setNewPassword1(e.target.value)}
        placeholder={t('Enter new password')}
        required
      />
    ),
    [t('Verify New Password')]: (
      <input
        type="password"
        value={newPassword2}
        className="passwordVerifyInput"
        onChange={(e) => setNewPassword2(e.target.value)}
        placeholder={t('Re-enter new password')}
        required
      />
    ),
    ...(passwordMessage && {
      '': <span className="errorText">{passwordMessage}</span>,
    }),
    ...(isSystemAdmin && {
      '': (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleGeneratePasswordClick}
          title={t('Generate new password and copy to clipboard')}
        >
          <AiOutlineUnlock style={{ marginRight: '4px' }} />
          {t('Generate Password')}
        </button>
      ),
    }),
  };

  const children = showChangePassword ? (
    <ModalFormFields fields={passwordFields} />
  ) : (
    <ModalFormFields fields={userFields} />
  );

  const footer = (
    <>
      <div className="footerLeft">
        {!showChangePassword && user.id > 0 && (
          <>
            <button
              type="button"
              className="btn btn-secondary changePasswordButton"
              onClick={handleChangePasswordClick}
              disabled={!canChangePassword}
              title={!canChangePassword ? t("You do not have permission to change this user's password") : t("Change Password")}
            >
              {t('Change Password')}
            </button>
            <button
              type="button"
              className="btn btn-danger iconButton"
              onClick={() => {
                if (canDeleteUser) deleteUserAsync();
              }}
              disabled={isDeleting || !canDeleteUser}
              title={!canDeleteUser ? t('You do not have permission to delete users') : (isDeleting ? t('Deleting...') : t('Delete User'))}
            >
              {isDeleting ? (
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              ) : (
                <IoTrashOutline />
              )}
            </button>
          </>
        )}
      </div>
      <div className="footerRight">
        {(showChangePassword || hasChanges) && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={showChangePassword ? handleChangePasswordClick : handleClose}
          >
            {showChangePassword ? t('Cancel Password Change') : t('Cancel')}
          </button>
        )}
        <button
          type="button"
          className={`btn btn-primary okButton ${showChangePassword
            ? (!isPasswordFormValid ? 'disabled' : '')
            : (!hasChanges || !isFormValid() ? 'disabled' : '')
            }`}
          onClick={showChangePassword ? handlePasswordSubmit : saveUserAsync}
        >
          {showChangePassword ? t('Update Password') : (user.id === 0 ? t('Create User') : t('Save'))}
        </button>
      </div>
    </>
  );

  return (userId !== null && userId !== undefined) ? (
    <>
      <Modal
        isOpen={isOpen}
        title={modalTitle}
        onClose={handleClose}
        footer={footer}
        className={`userEditModal ${showChangePassword ? 'expanded' : ''}`}
        updateSpinner={companyData?.updateSpinner} // Pass spinner update function if available
      >
        {children}
      </Modal>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title={t('Delete User')}
        message={t('Are you sure you want to delete user "{fullName}"?', 'Are you sure you want to delete user "{fullName}"?', { fullName: `${user.firstName} ${user.lastName}`.trim() })}
        confirmText={t('Yes, Delete User')}
        dangerText={t('This will permanently remove the user and all associated data.')}
        showSecondStep={false}
        secondStepMessage={t('Delete {fullName} permanently?', 'Delete {fullName} permanently?', { fullName: `${user.firstName} ${user.lastName}`.trim() })}
        secondStepWarning={t('This user and all their data will be permanently removed from the system.')}
        secondStepDetail={t('This includes their test results, training records, and profile information. This action cannot be undone.')}
      />
    </>
  ) : null
}

